// Lista de carteiras em tabela: busca, seleção múltipla para exclusão, edição

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Download, Upload, Search, Loader, ArrowLeft, Edit2, Trash2 } from 'lucide-react';
import { useWallets } from '@/contexts/WalletContext';
import { useToast } from '@/hooks/useToast';
import { formatAddress } from '@/utils/formatters';
import AddWalletForm from './AddWalletForm';
import ConfirmationModal from '../modals/ConfirmationModal';

export default function WalletList({ onClose, addressToAdd, walletDataToAdd, clearAddressToAdd, clearWalletDataToAdd }) {
  const { t } = useTranslation();
  const { wallets, loading, searchWallets, exportWallets, importWallets, deleteWallet } = useWallets();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [walletToEdit, setWalletToEdit] = useState(null);
  const [initialAddress, setInitialAddress] = useState(null);
  const [initialWalletData, setInitialWalletData] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { success, error: showError } = useToast();

  React.useEffect(() => {
    if (walletDataToAdd?.address?.trim()) {
      setInitialWalletData(walletDataToAdd);
      setInitialAddress(walletDataToAdd.address.trim());
      setWalletToEdit(null);
      setShowAddForm(true);
    } else if (addressToAdd?.trim()) {
      setInitialWalletData(null);
      setInitialAddress(addressToAdd.trim());
      setWalletToEdit(null);
      setShowAddForm(true);
    }
  }, [addressToAdd, walletDataToAdd]);

  const filteredWallets = searchTerm 
    ? searchWallets(searchTerm)
    : wallets;

  const handleExport = () => {
    const data = exportWallets ? exportWallets() : JSON.stringify(wallets, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `carteiras-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    success(t('wallets.exported'));
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !importWallets) return;
      setImporting(true);
      try {
        const text = await file.text();
        const ok = await importWallets(text, true);
        if (ok) success(t('wallets.imported'));
        else showError(t('wallets.importError'));
      } catch (err) {
        showError(err?.message || t('wallets.importErrorGeneric'));
      } finally {
        setImporting(false);
      }
    };
    input.click();
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredWallets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredWallets.map((w) => w.id)));
    }
  };

  const handleDeleteSelectedClick = () => {
    if (selectedIds.size === 0) return;
    setShowDeleteConfirm(true);
  };

  const handleDeleteSelectedConfirm = async () => {
    setShowDeleteConfirm(false);
    setDeleting(true);
    try {
      for (const id of selectedIds) {
        await deleteWallet(id);
      }
      setSelectedIds(new Set());
      success(t('wallets.deleteSuccess'));
    } catch (err) {
      showError(err?.message || t('wallets.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (wallet) => {
    setWalletToEdit(wallet);
    setShowAddForm(true);
  };

  const closeForm = () => {
    setShowAddForm(false);
    setWalletToEdit(null);
    setInitialAddress(null);
    setInitialWalletData(null);
    clearAddressToAdd?.();
    clearWalletDataToAdd?.();
    clearAddressToAdd?.();
  };

  return (
    <>
      <div className="bg-gray-800 bg-opacity-50 backdrop-blur-xl rounded-3xl p-6 border border-purple-500 border-opacity-20">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition"
                title={t('wallets.backToTransfer')}
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <h2 className="text-2xl font-bold text-white">{t('wallets.title')}</h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              disabled={wallets.length === 0}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white p-2 rounded-lg transition disabled:cursor-not-allowed"
              title={t('wallets.exportTitle')}
            >
              <Download size={20} />
            </button>
            <button
              onClick={handleImport}
              disabled={importing}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white p-2 rounded-lg transition disabled:cursor-wait"
              title={t('wallets.importTitle')}
            >
              {importing ? <Loader size={20} className="animate-spin" /> : <Upload size={20} />}
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white p-2 rounded-lg transition"
              title={t('wallets.addTitle')}
            >
              <Plus size={20} />
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-500" size={20} />
            <input
              type="text"
              placeholder={t('wallets.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-700 text-white rounded-lg p-3 pl-10 placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin mb-4 inline-block">
                <Loader size={32} className="text-purple-400" />
              </div>
              <p className="text-gray-400">{t('wallets.loadingWallets')}</p>
            </div>
          </div>
        ) : filteredWallets.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 mb-4">
              {wallets.length === 0 
                ? t('wallets.noneAdded')
                : t('wallets.noneFound')}
            </p>
            {wallets.length === 0 && (
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition"
              >
                <Plus size={20} />
                {t('wallets.addFirst')}
              </button>
            )}
          </div>
        ) : (
          <>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-3 py-2 mb-4">
                <span className="text-sm text-gray-400">
                  {t('wallets.selectedCount', { count: selectedIds.size })}
                </span>
                <button
                  type="button"
                  onClick={handleDeleteSelectedClick}
                  disabled={deleting}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm transition"
                >
                  {deleting ? <Loader size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  {t('wallets.deleteSelected')}
                </button>
              </div>
            )}

            <div className="rounded-xl overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-600">
                    <th className="text-left py-3 px-3 w-10">
                      <input
                        type="checkbox"
                        checked={filteredWallets.length > 0 && selectedIds.size === filteredWallets.length}
                        onChange={toggleSelectAll}
                        className="rounded bg-gray-700 border-gray-600 text-purple-500 focus:ring-purple-500"
                      />
                    </th>
                    <th className="text-left py-3 px-3 text-sm font-semibold text-gray-400">{t('wallets.name')}</th>
                    <th className="text-left py-3 px-3 text-sm font-semibold text-gray-400">{t('wallets.email')}</th>
                    <th className="text-left py-3 px-3 text-sm font-semibold text-gray-400">{t('wallets.network')}</th>
                    <th className="text-left py-3 px-3 text-sm font-semibold text-gray-400">{t('wallets.address')}</th>
                    <th className="w-10 py-3 px-3" />
                  </tr>
                </thead>
                <tbody>
                  {filteredWallets.map((wallet) => (
                    <tr
                      key={wallet.id}
                      className="border-b border-gray-700 hover:bg-gray-700/30 transition"
                    >
                      <td className="py-3 px-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(wallet.id)}
                          onChange={() => toggleSelect(wallet.id)}
                          className="rounded bg-gray-700 border-gray-600 text-purple-500 focus:ring-purple-500"
                        />
                      </td>
                      <td className="py-3 px-3 text-white font-medium">{wallet.name}</td>
                      <td className="py-3 px-3 text-gray-300 text-sm">{wallet.email || '—'}</td>
                      <td className="py-3 px-3 text-gray-300 text-sm capitalize">{wallet.network || '—'}</td>
                      <td className="py-3 px-3 text-gray-400 text-sm font-mono">
                        {formatAddress(wallet.address, 8, 6)}
                      </td>
                      <td className="py-3 px-3 text-right">
                        <button
                          type="button"
                          onClick={() => openEdit(wallet)}
                          className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-600 transition"
                          title={t('wallets.editTitle')}
                        >
                          <Edit2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <AddWalletForm
        isOpen={showAddForm}
        onClose={closeForm}
        wallet={walletToEdit}
        initialAddress={initialAddress}
        initialWalletData={initialWalletData}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title={t('wallets.deleteConfirmTitle')}
        message={t('wallets.deleteConfirmMessage', { count: selectedIds.size })}
        confirmText={t('wallets.deleteConfirmButton')}
        cancelText={t('modal.cancel')}
        isDangerous
        onConfirm={handleDeleteSelectedConfirm}
      />
    </>
  );
}
